import { useQuery } from "@apollo/client";
import CheckIcon from "@mui/icons-material/Check";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Box,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { Dialog } from "components/Dialog";
import { gql } from "generated-graphql";
import { ExtractionStatus, JobStatus } from "generated-graphql/graphql";
import pluralize from "pluralize";
import { useEffect, useMemo } from "react";

const DOCUMENT_AI_ENRICHMENT_JOB_STATUS = gql(`
  query DocumentAIEnrichmentJob($id: ID!) {
    documentAIEnrichmentJob(id: $id) {
      id
      status
      documents {
        id
        title
        extractionStatus
        lastExtractionAt
        extractedFilename
      }
    }
  }
`);

export function DocumentAIAnalyzingDialog({
  open,
  onClose,
  jobId,
  fileNames,
}: {
  open: boolean;
  onClose:
    | (() => void)
    | ((
        event: React.MouseEvent<HTMLElement>,
        reason:
          | "backdropClick"
          | "escapeKeyDown"
          | "cancelButtonClick"
          | "closeButtonClick"
      ) => void);
  jobId: string | null;
  fileNames: string[];
}) {
  const { data, stopPolling } = useQuery(DOCUMENT_AI_ENRICHMENT_JOB_STATUS, {
    variables: { id: jobId ?? "" },
    pollInterval: open && jobId ? 1000 : 0,
    fetchPolicy: "network-only",
    skip: !open || !jobId,
  });

  const status: JobStatus | undefined = data?.documentAIEnrichmentJob?.status;
  const isRunning =
    status === JobStatus.Queued ||
    status === JobStatus.Dispatched ||
    status === JobStatus.Running ||
    status === undefined; // initial state
  const isSucceeded = status === JobStatus.Succeeded;
  const isFailed = status === JobStatus.Failed;

  useEffect(() => {
    if (status === JobStatus.Succeeded || status === JobStatus.Failed) {
      stopPolling();
    }
  }, [status, stopPolling]);

  const analysisStats = useMemo(() => {
    if (!isSucceeded) {
      return undefined;
    }
    return {
      totalDocuments: data?.documentAIEnrichmentJob?.documents?.length,
      succeededDocuments:
        data?.documentAIEnrichmentJob?.documents?.filter(
          (document) => document.extractionStatus === ExtractionStatus.Succeeded
        ).length ?? 0,
    };
  }, [data?.documentAIEnrichmentJob?.documents, isSucceeded]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      title={`Analyzing ${fileNames.length} ${pluralize(
        "Document",
        fileNames.length
      )}`}
      primaryActionButtonProps={{
        saveText: "Review and Confirm",
        disabled: !isSucceeded,
      }}
      cancelButtonProps={{ text: isRunning ? "Close" : "Done" }}
    >
      <Stack spacing={2}>
        <Box>
          <LinearProgress
            value={isRunning ? undefined : 100}
            variant={isRunning ? "indeterminate" : "determinate"}
          />

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {isRunning &&
              "Hang tight, we're reading your uploads and filling in what we can. This usually takes about 5-10 seconds per file, though larger files may take longer. We'll let you know when everything is ready for you to review."}
            {isSucceeded &&
              `Analysis complete. ${analysisStats?.succeededDocuments} of ${
                analysisStats?.totalDocuments
              } ${pluralize(
                "document",
                analysisStats?.totalDocuments
              )} analyzed successfully.`}
            {isFailed && "Analysis failed."}
          </Typography>
        </Box>

        <List dense>
          {fileNames.map((fileName) => {
            const documentRecord =
              data?.documentAIEnrichmentJob?.documents?.find(
                (document) => document.title === fileName
              );
            return (
              <ListItem key={documentRecord?.id ?? fileName}>
                <ListItemText primary={documentRecord?.title ?? fileName} />
                <ListItemText
                  sx={{ textAlign: "right" }}
                  primary={displayExtractionStatus(
                    documentRecord?.extractionStatus
                  )}
                />
              </ListItem>
            );
          })}
        </List>
      </Stack>
    </Dialog>
  );
}

const displayExtractionStatus = (
  extractionStatus: ExtractionStatus | undefined
) => {
  switch (extractionStatus) {
    case ExtractionStatus.Pending:
    case ExtractionStatus.NotAttempted:
    case undefined:
      return "In progress...";
    case ExtractionStatus.Succeeded:
      return <CheckIcon color="success" />;
    case ExtractionStatus.Failed:
      return <ErrorOutlineIcon color="error" />;
  }
};
